import { auth } from '../firebase'; // Import auth to get the current user's token

// Define API_BASE_URL once at the top level
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3800';

/**
 * Fetches a list of jobs from the backend API based on user type and ID.
 * @param {object} options - Filtering and pagination options.
 * @param {string} options.userType - The type of the user (e.g., 'franchisee').
 * @param {string} options.fseId - The franchisee ID (required if userType is 'franchisee').
 * @param {string} [options.status] - Filter jobs by status (could be empty string for 'All').
 * @param {string} [options.householdId] - Filter jobs by household ID.
 * @param {number} [options.pageSize=10] - Number of jobs per page.
 * @param {string} [options.orderBy='jobDate'] - Field to order by.
 * @param {string} [options.orderDirection='asc'] - Order direction ('asc' or 'desc').
 * @param {string} [options.startAfterDocId=null] - Firestore document ID to start after for pagination.
 * @returns {Promise<object>} - A promise that resolves with the job list data.
 * @throws {Error} - Throws an error if the request fails or the response is not successful.
 */
export const listJobs = async (options) => {
  const {
    userType,
    fseId,
    status, // Receive status filter (could be empty string for 'All')
    householdId, // Added householdId
    pageSize = 10,
    orderBy = 'jobDate', // Default to sorting by job date
    orderDirection = 'asc', // Default to ascending (earliest jobs first)
    startAfterDocId = null,
    view, // 'franchisee' when admin views from franchisee app
  } = options;

  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();

  const requestBody = {
    userType,
    fseId,
    pageSize,
    orderBy,
    orderDirection,
    startAfterDocId, // Include pagination parameter
  };

  if (view) requestBody.view = view;

  // Conditionally add status to the request body if it's not empty
  if (status) {
    requestBody.status = status;
  }

  // Conditionally add householdId to the request body if it's provided
  if (householdId) {
    requestBody.householdId = householdId;
  }

  // Log the request body before sending
  console.log("API Request to /job/list:", JSON.stringify(requestBody, null, 2));

  try {
    const response = await fetch(`${API_BASE_URL}/job/list`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log("API Response from /job/list:", result); // Debugging log

    if (result.status === "success" && result.data) {
      return result.data; // Contains { jobs: [], nextPageDocId: '...' | null }
    } else {
      throw new Error(result.message || 'Failed to list jobs from API.');
    }
  } catch (error) {
    console.error("Error listing jobs:", error);
    throw error;
  }
};

/**
 * Fetches the franchisee's job list from the dedicated franchisee endpoint. All filtering,
 * grouping (approved collapse + supersession) and sorting is done server-side; the response
 * is display-ready.
 * @param {object} options
 * @param {string} [options.status] - 'approved', '' (all), or a specific status.
 * @param {string} [options.householdId] - Optional household filter.
 * @param {string} [options.sort] - 'jobDate-asc' etc.
 * @param {number} [options.pageSize] - Page size for flat lists.
 * @param {string} [options.startAfterDocId] - Pagination cursor.
 * @param {string} [options.fseId] - Target franchisee (only when viewing as admin).
 * @returns {Promise<object>} - { jobs, nextPageDocId, counts?, missedTotal?, missedTruncated? }
 */
export const listFranchiseeJobs = async (options = {}) => {
  const { status, householdId, sort, pageSize, startAfterDocId, fseId } = options;

  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const requestBody = {
    status: status ?? null,
    sort: typeof sort === 'string' ? sort : '',
    timezone,
  };
  if (fseId) requestBody.fseId = fseId;
  if (householdId) requestBody.householdId = householdId;
  if (pageSize) requestBody.pageSize = pageSize;
  if (startAfterDocId) requestBody.startAfterDocId = startAfterDocId;

  try {
    const response = await fetch(`${API_BASE_URL}/job/franchisee/list`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || `API request failed: ${response.status}`);
    }
    if (result.status !== 'success') {
      throw new Error(result.message || 'Failed to list jobs.');
    }

    return result.data; // { jobs, nextPageDocId, counts?, missedTotal?, missedTruncated? }
  } catch (error) {
    console.error("Error listing franchisee jobs:", error);
    throw error;
  }
};

/**
 * Fetches details for a specific job from the backend API using POST.
 * @param {string} jobId - The ID of the job to fetch.
 * @returns {Promise<object>} - A promise that resolves with the job details data.
 * @throws {Error} - Throws an error if the request fails or the response is not successful.
 */
export const getJobDetails = async (jobId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!jobId) {
    throw new Error("Job ID is required.");
  }

  const idToken = await user.getIdToken();
  const requestBody = { jobId }; // Prepare body

  // Log request details
  console.log(`API Request POST to /job/details with body:`, JSON.stringify(requestBody, null, 2));

  try {
    // Use POST method and send jobId in the body
    const response = await fetch(`${API_BASE_URL}/job/details`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorMsg = `API request failed: ${response.status}`;
      try {
        const errorBody = await response.json();
        if (errorBody.message) errorMsg = errorBody.message;
      } catch {}
      throw new Error(errorMsg);
    }

    const result = await response.json();
    console.log(`API Response from /job/details:`, result); // Log response

    // Assuming the backend returns { status: 'success', data: { ...jobDetails } }
    if (result.status === "success" && result.data) {
      return result.data;
    } else {
      throw new Error(result.message || `Failed to get job details for ID ${jobId}.`);
    }
  } catch (error) {
    console.error(`Error fetching job details for ID ${jobId}:`, error);
    throw error;
  }
};

/**
 * Uploads job images to the backend API.
 * @param {FormData} formData - The FormData object containing the image files under the key 'jobImages'.
 * @returns {Promise<object>} - A promise that resolves with the upload result data (containing URLs).
 * @throws {Error} - Throws an error if the request fails or the response is not successful.
 */
export const uploadJobImages = async (formData) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();

  console.log(`API Request POST to /upload/job-images`); // Log request
  // Log the start of the token to verify it's being fetched
  console.log("Using token starting with:", idToken ? idToken.substring(0, 20) + "..." : "No Token");

  let response; // Define response outside try to access in catch
  try {
    response = await fetch(`${API_BASE_URL}/upload/job-images`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: formData,
    });

    // Check if response is OK *before* trying to parse JSON
    if (!response.ok) {
      let errorText = '';
      try {
        // Attempt to get error message from response body (might be HTML or plain text)
        errorText = await response.text();
        console.error("Server returned non-OK status:", response.status, response.statusText, "Response Body:", errorText);
      } catch (textError) {
        // If reading text fails, just use status text
        console.error("Server returned non-OK status:", response.status, response.statusText, "Could not read response body.");
      }
      // Throw a more informative error
      throw new Error(`HTTP error! Status: ${response.status} ${response.statusText}. ${errorText.substring(0, 150)}`);
    }

    // Only parse JSON if the response was OK
    const result = await response.json();
    console.log(`API Response from /upload/job-images:`, result); // Log response

    if (result.status === "success" && result.data) {
      return result.data; // Contains { urls: [...] }
    } else {
      // Handle cases where status is OK but backend indicates failure in JSON
      throw new Error(result.message || 'Image upload failed according to API response.');
    }
  } catch (error) {
    // Log the caught error (could be the one thrown above or a network/parsing error)
    console.error('Error during uploadJobImages fetch:', error);

    // If it's a SyntaxError (JSON parse failed), it implies response.ok was true,
    // but the body wasn't valid JSON. This shouldn't happen with the check above, but good fallback.
    if (error instanceof SyntaxError && response) {
        console.error("Response body was not valid JSON, despite OK status (unexpected).");
        // Optionally try to log raw text again if needed, though the !response.ok block should catch most non-JSON cases.
    }

    // Re-throw the error so the component can handle it
    throw error;
  }
};

/**
 * Deletes a specific job image via the backend API.
 * @param {string} imageUrl - The URL of the image to delete.
 * @returns {Promise<object>} - A promise that resolves with the success message from the API.
 * @throws {Error} - Throws an error if the request fails or the response is not successful.
 */
export const deleteJobImage = async (imageUrl) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!imageUrl) {
    throw new Error("Image URL is required for deletion.");
  }

  const idToken = await user.getIdToken();
  const requestBody = { imageUrl };

  console.log(`API Request POST to /upload/delete-image`); // Log request

  let response;
  try {
    response = await fetch(`${API_BASE_URL}/upload/delete-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json', // Sending JSON body
      },
      body: JSON.stringify(requestBody),
    });

    // Check if response is OK *before* trying to parse JSON
    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
        console.error("Server returned non-OK status for delete:", response.status, response.statusText, "Response Body:", errorText);
      } catch (textError) {
        console.error("Server returned non-OK status for delete:", response.status, response.statusText, "Could not read response body.");
      }
      throw new Error(`HTTP error! Status: ${response.status} ${response.statusText}. ${errorText.substring(0, 150)}`);
    }

    const result = await response.json();
    console.log(`API Response from /upload/delete-image:`, result);

    if (result.status === "success") {
      return result; // Contains { status: 'success', message: '...' }
    } else {
      throw new Error(result.message || 'Image deletion failed according to API response.');
    }
  } catch (error) {
    console.error('Error during deleteJobImage fetch:', error);
    throw error;
  }
};

/**
 * Updates specific fields of a job via the backend API.
 * @param {string} jobId - The ID of the job to update.
 * @param {object} updateData - An object containing the fields to update (e.g., { status: 'completed', actualHours: 3.5, photoUrls: [...] }).
 * @returns {Promise<object>} - A promise that resolves with the success message from the API.
 * @throws {Error} - Throws an error if the request fails or the response is not successful.
 */
export const updateJobDetails = async (jobId, updateData) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!jobId) {
    throw new Error("Job ID is required for update.");
  }
  if (!updateData || Object.keys(updateData).length === 0) {
    console.warn("Update called with no data to change.");
    return { status: "success", message: "No changes submitted." }; // Or throw an error if preferred
  }

  // Ensure actualHours is a number if present
  if (updateData.actualHours !== undefined && updateData.actualHours !== null && updateData.actualHours !== '') {
      const hours = parseFloat(updateData.actualHours);
      if (isNaN(hours)) {
          throw new Error("Invalid format for Actual Hours.");
      }
      updateData.actualHours = hours;
  } else if (updateData.hasOwnProperty('actualHours')) {
      // If actualHours is explicitly set to empty string or null, send null
      updateData.actualHours = null;
  }

  const idToken = await user.getIdToken();
  // Include jobId in the body along with updateData, plus user's timezone for date validation
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const requestBody = { jobId, ...updateData, userTimezone };

  console.log(`API Request POST to /job/update with body:`, JSON.stringify(requestBody, null, 2)); // Log request

  let response;
  try {
    response = await fetch(`${API_BASE_URL}/job/update`, { // Assuming endpoint is /job/update
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    // Check if response is OK *before* trying to parse JSON
    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
        console.error("Server returned non-OK status for update:", response.status, response.statusText, "Response Body:", errorText);
      } catch (textError) {
        console.error("Server returned non-OK status for update:", response.status, response.statusText, "Could not read response body.");
      }
      throw new Error(`HTTP error! Status: ${response.status} ${response.statusText}. ${errorText.substring(0, 150)}`);
    }

    const result = await response.json();
    console.log(`API Response from /job/update:`, result);

    if (result.status === "success") {
      return result; // Contains { status: 'success', message: '...' }
    } else {
      throw new Error(result.message || 'Job update failed according to API response.');
    }
  } catch (error) {
    console.error('Error during updateJobDetails fetch:', error);
    throw error;
  }
};

/**
 * Fetches a list of job schedules from the backend API.
 * Typically used for fetching jobs with status 'pending-quote'.
 * @param {object} options - Filtering and pagination options.
 * @param {string} options.fseId - The franchisee ID.
 * @param {string} options.status - Filter jobs by status (e.g., 'pending-quote').
 * @param {number} [options.pageSize=10] - Number of schedules per page.
 * @param {string} [options.orderBy='startDate'] - Field to order by.
 * @param {string} [options.orderDirection='asc'] - Order direction ('asc' or 'desc').
 * @param {string} [options.startAfterDocId=null] - Document ID to start after for pagination.
 * @returns {Promise<object>} - A promise that resolves with the job schedules list data (schedules and nextPageDocId).
 * @throws {Error} - Throws an error if the request fails or the response is not successful.
 */
export const listJobSchedules = async (options) => {
  const { fseId, quoteStatus, pageSize = 10, orderBy = 'startDate', orderDirection = 'asc', startAfterDocId } = options;

  if (!fseId) {
    throw new Error('Franchisee ID (fseId) is required to list job schedules.');
  }
  if (!quoteStatus) {
    throw new Error('Status is required to list job schedules.');
  }

  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  const body = {
    fseId,
    quoteStatus,
    pageSize,
    orderBy,
    orderDirection,
  };

  if (startAfterDocId) {
    body.startAfterDocId = startAfterDocId;
  }

  const response = await fetch(`${API_BASE_URL}/schedule/list`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });

  console.log("API Request to /schedule/list:", JSON.stringify(body, null, 2)); // Log request
  

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to fetch job schedules and could not parse error response.' }));
    console.error('API Error Data for listJobSchedules:', errorData); 
    throw new Error(errorData.message || `HTTP error ${response.status} while fetching job schedules.`);
  }

  const result = await response.json();
  if (result.status !== 'success') {
    throw new Error(result.message || 'Failed to list job schedules due to an API issue.');
  }
  console.log("API Response from /schedule/list:", result.data); // Log response
  return result.data; // Contains { schedules: [], nextPageDocId: ... }
};

// Helper function to format Firestore Timestamp (can be moved to a utils file)
export const formatTimestamp = (timestamp, includeTime = false) => {
  let date;
  // Handle string dates (like completionDate or potentially updated jobDate)
  if (typeof timestamp === 'string') {
    try {
      // Attempt to parse YYYY-MM-DD or ISO string
      date = new Date(timestamp);
      // Check if the date is valid after parsing
      if (isNaN(date.getTime())) {
         console.warn("Could not parse string date:", timestamp);
         return timestamp; // Return original string if parsing fails
      }
      // Adjust for potential timezone offset if just YYYY-MM-DD was parsed
      // This ensures the date displayed matches the input date regardless of local timezone
      date = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

    } catch (e) {
      console.warn("Could not parse string date:", timestamp, e);
      return timestamp; // Return original string if parsing fails
    }
  }
  // Handle Firestore Timestamp objects
  else if (timestamp && typeof timestamp.toDate === 'function') {
    date = timestamp.toDate();
  } else if (timestamp && typeof timestamp._seconds === 'number') {
    date = new Date(timestamp._seconds * 1000);
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else {
    return 'N/A'; // Not a recognizable date format
  }

  if (isNaN(date.getTime())) return 'N/A'; // Final check for invalid date

  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    options.hour12 = true; // Use AM/PM
  }
  // Use Australian locale for date formatting if desired, otherwise use default
  return date.toLocaleDateString('en-AU', options);
};

// Helper function to format status (can be moved to a utils file)
export const formatStatus = (status) => {
    if (!status) return 'N/A';
    const formatted = status.replace(/-/g, ' ');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

/**
 * Formats frequency type and value into a readable string.
 * @param {string} frequencyType - The type of frequency (e.g., 'weekly', 'monthly', 'days', 'Weeks', 'Months').
 * @param {number} frequencyValue - The value associated with the type (e.g., 2 for every 2 weeks).
 * @returns {string} - A formatted frequency string (e.g., "Every 2 weeks", "Monthly", "Every 10 days").
 */
export const formatFrequency = (frequencyType, frequencyValue) => {
    if (!frequencyType) return '';

    const value = frequencyValue || 1; // Default to 1 if value is missing or 0
    const typeLower = frequencyType.toLowerCase();

    // Handle plural/singular variations
    if (typeLower.startsWith('week')) {
        return value === 1 ? 'Weekly' : `Every ${value} weeks`;
    } else if (typeLower.startsWith('month')) {
        return value === 1 ? 'Monthly' : `Every ${value} months`;
    } else if (typeLower.startsWith('day')) {
        return value === 1 ? 'Daily' : `Every ${value} days`;
    } else if (typeLower.startsWith('year')) {
        return value === 1 ? 'Yearly' : `Every ${value} years`;
    } else {
        // Fallback for unknown types, trying to make it readable
        return `Every ${value} ${frequencyType}${value !== 1 ? 's' : ''}`; // Simple pluralization
    }
};

/**
 * Formats allocation type into a readable string.
 * @param {string} allocationType - The type of allocation (e.g., 'per-visit', 'fixed-fee', 'monthly').
 * @returns {string} - A formatted allocation string (e.g., "Per visit", "Fixed", "Monthly", "N/A").
 */
export const formatAllocationType = (allocationType) => {
    if (!allocationType) return 'N/A';

    switch (allocationType.toLowerCase()) {
        case 'per-visit':
            return 'Per visit';
        case 'fixed-fee':
            return 'Fixed Price';
        case 'monthly':
            return 'Monthly';
        default:
            return allocationType; // Return original if unknown
    }
};

/**
 * Formats allocation details combining hours and frequency into a single string.
 * Similar to the email notification format.
 * @param {object} job - Job object containing allocation details
 * @returns {string} - Formatted allocation string (e.g., "4 hours per month", "2 hours every 2 weeks", "Fixed fee: $150.00")
 */
export const formatAllocationDetails = (job) => {
    if (!job) return 'N/A';
    
    const allocationType = job.allocationType;
    const hours = job.allotedHours || 0;
    const allocationValue = Number(job.allocationValue) || null;
    const frequencyType = typeof job.frequencyType === 'string' ? job.frequencyType.toLowerCase() : null;
    const frequencyValue = Number(job.frequencyValue) || null;

    if (allocationType === 'monthly') {
        // Monthly allocation: show hours per month
        return `${hours} hours per month`;
    } else if (allocationType === 'days' || allocationType === 'weeks') {
        // Prefer the recurrence frequency, matching the admin app. Jobs are never written with an
        // allocationValue (all 2190 weeks/days jobs in prod lack one) while they do carry
        // frequencyValue, so reading allocationValue alone rendered every fortnightly job as
        // "every 1 week" on the franchisee's card.
        const useFrequency = job.scheduleType === 'recurring' && (frequencyType === 'days' || frequencyType === 'weeks');
        const intervalType = useFrequency ? frequencyType : allocationType;
        const intervalValue = useFrequency ? frequencyValue : allocationValue;
        // With no interval to show, state the hours rather than invent a period.
        if (!intervalValue) return `${hours} hours`;
        const allocationUnit = intervalType === 'days'
            ? (intervalValue === 1 ? 'day' : 'days')
            : (intervalValue === 1 ? 'week' : 'weeks');
        return `${hours} hours every ${intervalValue} ${allocationUnit}`;
    } else if (allocationType === 'fixed-fee' || allocationType === 'set-fee') {
        // Fixed fee allocation
        return `Fixed fee`;
    } else {
        // Default to showing allotted hours if available
        return `${hours} hours`;
    }
};

/**
 * Generates a Google Maps search URL from address information.
 * @param {object} addressInfo - Object containing address parts (addressLine1, addressLine2, state, postalCode).
 * @returns {string|null} - The Google Maps URL or null if no address parts are provided.
 */
export const getGoogleMapsUrl = (addressInfo) => {
  if (!addressInfo) return null;
  const { addressLine1, addressLine2, state, postalCode } = addressInfo;
  const addressParts = [addressLine1, addressLine2, state, postalCode].filter(part => part && part.trim() !== '');
  if (addressParts.length === 0) return null;
  const query = encodeURIComponent(addressParts.join(', '));
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
};

/**
 * Submits a quote price for a pending quote job.
 * @param {string} scheduleId - The ID of the job schedule to update.
 * @param {number} pricePerHour - The quoted price per hour.
 * @param {string} [notesFromFranchisee] - Optional notes from the franchisee to include with the quote.
 * @param {string} [frequencyType] - Optional frequency type ('Monthly', 'Days', 'Weeks').
 * @param {number} [frequencyValue] - Optional frequency value (required for 'Days' and 'Weeks').
 * @returns {Promise<object>} - A promise that resolves with the success message from the API.
 * @throws {Error} - Throws an error if the request fails or the response is not successful.
 */
export const submitQuote = async (scheduleId, pricePerHour, notesFromFranchisee, frequencyType, frequencyValue) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }
  if (!scheduleId) {
    throw new Error("Schedule ID is required for quote submission.");
  }
  if (typeof pricePerHour !== 'number' || pricePerHour <= 0) {
    throw new Error("Valid price per hour is required.");
  }

  // Validate frequency data if provided
  if (frequencyType && frequencyType !== 'Monthly') {
    if (!frequencyValue || !Number.isInteger(frequencyValue) || frequencyValue <= 0) {
      throw new Error("Valid frequency value is required for interval-based frequencies.");
    }
  }

  const idToken = await user.getIdToken();
  const requestBody = { 
    scheduleId,
    pricePerHour,
    quoteStatus: 'quote-received' // Updated to use new quote-received status
  };

  // Include notes from franchisee if provided
  if (notesFromFranchisee && notesFromFranchisee.trim()) {
    requestBody.notesFromFranchisee = notesFromFranchisee.trim();
  }

  // Include frequency data if provided
  if (frequencyType) {
    requestBody.frequencyType = frequencyType;
    if (frequencyType !== 'Monthly' && frequencyValue) {
      requestBody.frequencyValue = frequencyValue;
    }
  }

  console.log(`API Request POST to /schedule/edit with body:`, JSON.stringify(requestBody, null, 2));

  try {
    const response = await fetch(`${API_BASE_URL}/schedule/edit`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);
        errorText = errorJson.message || errorText;
      } catch (e) {
        // Not a JSON error response, use the text as is
      }
      throw new Error(`HTTP error! Status: ${response.status} ${response.statusText}. ${errorText.substring(0, 150)}`);
    }

    const result = await response.json();
    console.log(`API Response from /schedule/edit:`, result);

    if (result.status === "success") {
      return result;
    } else {
      throw new Error(result.message || 'Quote submission failed according to API response.');
    }
  } catch (error) {
    console.error('Error during quote submission:', error);
    throw error;
  }
};

/**
 * Validate proposed hours against monthly allocation limits
 * @param {string} jobId - The job ID
 * @param {number} proposedHours - The proposed hours to validate
 * @returns {Promise<object>} Validation result with remaining allocation info
 */
export const validateMonthlyHours = async (jobId, proposedHours) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  if (!jobId) {
    throw new Error("Job ID is required.");
  }

  if (proposedHours === undefined || proposedHours === null) {
    throw new Error("Proposed hours is required.");
  }

  const idToken = await user.getIdToken();
  const requestBody = { jobId, proposedHours };

  console.log(`API Request POST to /job/validate-monthly-hours with body:`, JSON.stringify(requestBody, null, 2));

  try {
    const response = await fetch(`${API_BASE_URL}/job/validate-monthly-hours`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);
        errorText = errorJson.message || errorText;
      } catch (e) {
        // Not a JSON error response, use the text as is
      }
      throw new Error(`HTTP error! Status: ${response.status} ${response.statusText}. ${errorText.substring(0, 150)}`);
    }

    const result = await response.json();
    console.log(`API Response from /job/validate-monthly-hours:`, result);

    return result;
  } catch (error) {
    console.error('Error during monthly hours validation:', error);
    throw error;
  }
};

/**
 * Completes all remaining jobs in a monthly allocation schedule
 * @param {string} scheduleId - The schedule ID
 * @param {string} completionDate - The completion date
 * @returns {Promise<Object>} Result of the operation
 */
export const completeAllJobsInSchedule = async (scheduleId, completionDate) => {
  try {
    console.log(`[DEBUG] Completing all jobs in schedule ${scheduleId}`);

    // Validate completionDate before sending to API
    if (!completionDate || typeof completionDate !== 'string' || completionDate.trim() === '') {
      throw new Error("Completion date is required and must be a valid date string");
    }

    const parsedDate = new Date(completionDate);
    if (isNaN(parsedDate.getTime())) {
      throw new Error("Completion date must be a valid date format");
    }

    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated.");
    }

    const idToken = await user.getIdToken();
    const response = await fetch(`${API_BASE_URL}/job/complete-schedule`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ scheduleId, completionDate, userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: "Unknown error" }));
      throw new Error(errorData.message || "Failed to complete schedule jobs");
    }

    const result = await response.json();
    console.log(`[DEBUG] Complete schedule result:`, result);
    return result;
  } catch (error) {
    console.error(`[DEBUG] Error completing schedule jobs:`, error);
    throw error;
  }
};

/**
 * Checks for completed or invoiced jobs within 7 days of a proposed completion date
 * for the same client and service type.
 * @param {object} options - Options for the check.
 * @param {string} options.jobId - The current job ID (to exclude from results).
 * @param {string} options.fseId - The franchisee ID.
 * @param {string} options.householdId - The household/client ID.
 * @param {Array} options.services - Array of service objects {name, code}.
 * @param {string} options.proposedCompletionDate - The proposed completion date (YYYY-MM-DD string).
 * @returns {Promise<Array>} - Array of nearby jobs matching the criteria.
 * @throws {Error} - Throws an error if the request fails.
 */
export const checkNearbyCompletedJobs = async (options) => {
  const { jobId, fseId, householdId, scheduleId, services, proposedCompletionDate, frequencyType, frequencyValue } = options;

  if (!fseId || !householdId || !services || !proposedCompletionDate) {
    console.warn('[checkNearbyCompletedJobs] Missing required parameters');
    return [];
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();

  // Parse the proposed completion date
  const proposedDate = new Date(proposedCompletionDate);
  if (isNaN(proposedDate.getTime())) {
    console.warn('[checkNearbyCompletedJobs] Invalid proposedCompletionDate:', proposedCompletionDate);
    return [];
  }

  // Build service identifiers for the current job.
  // Jobs store services as { serviceTypeId, name } — the 'code' field is not populated.
  // Use serviceTypeId as the primary key, falling back to code then name.
  const getServiceKey = (s) => s.serviceTypeId || s.code || s.name;
  const currentServiceKeys = services.map(getServiceKey).filter(Boolean);

  // A visit one full cycle after the last one is the schedule working, not a duplicate. A flat 7-day
  // window flagged every job on an "Every 7 Days" schedule, so the window is kept below the
  // schedule's own cycle. Never widens the window, only narrows it for short cycles.
  const frequencyUnit = String(frequencyType || '').toLowerCase();
  const cycleDays = frequencyUnit === 'days' ? Number(frequencyValue)
    : frequencyUnit === 'weeks' ? Number(frequencyValue) * 7
      : null;
  const windowDays = cycleDays > 0 ? Math.min(7, cycleDays - 1) : 7;

  try {
    // Fetch completed jobs for the same household
    // We'll fetch jobs with 'completed' and 'invoiced' statuses
    const completedJobsResponse = await fetch(`${API_BASE_URL}/job/list`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userType: 'franchisee',
        fseId,
        householdId,
        status: 'completed',
        pageSize: 50, // Fetch enough to check recent jobs
        orderBy: 'completionDate',
        orderDirection: 'desc',
      }),
    });

    if (!completedJobsResponse.ok) {
      console.error('[checkNearbyCompletedJobs] Failed to fetch completed jobs');
      return [];
    }

    const completedResult = await completedJobsResponse.json();
    let allJobs = completedResult.status === 'success' && completedResult.data?.jobs
      ? completedResult.data.jobs
      : [];

    // Also fetch invoiced jobs
    const invoicedJobsResponse = await fetch(`${API_BASE_URL}/job/list`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userType: 'franchisee',
        fseId,
        householdId,
        status: 'invoiced',
        pageSize: 50,
        orderBy: 'completionDate',
        orderDirection: 'desc',
      }),
    });

    if (invoicedJobsResponse.ok) {
      const invoicedResult = await invoicedJobsResponse.json();
      if (invoicedResult.status === 'success' && invoicedResult.data?.jobs) {
        allJobs = [...allJobs, ...invoicedResult.data.jobs];
      }
    }

    console.log('[checkNearbyCompletedJobs] Found jobs for household:', allJobs.length);

    // Filter jobs:
    // 1. Exclude the current job
    // 2. Must be on the same schedule (different schedules for the same client are not duplicates)
    // 3. Match service type (at least one service code matches)
    // 4. Completion date within 7 days of proposed date
    const nearbyJobs = allJobs.filter(job => {
      // Exclude current job
      if (job.id === jobId) return false;

      // Only flag duplicates within the same schedule.
      // A client may have separate schedules (e.g. mowing + gardening) that legitimately
      // complete on the same day — those should never be blocked.
      if (scheduleId && job.scheduleId && job.scheduleId !== scheduleId) return false;

      // Check service type match — at least one service identifier must match.
      // Uses serviceTypeId as the primary key (how services are stored), falling back to code/name.
      const jobServiceKeys = (job.services || []).map(getServiceKey).filter(Boolean);
      const hasMatchingService = currentServiceKeys.length > 0
        && jobServiceKeys.length > 0
        && currentServiceKeys.some(key => jobServiceKeys.includes(key));
      if (!hasMatchingService) return false;

      // Check completion date proximity
      if (!job.completionDate) return false;

      let jobCompletionDate;
      if (typeof job.completionDate === 'string') {
        jobCompletionDate = new Date(job.completionDate);
      } else if (job.completionDate._seconds) {
        jobCompletionDate = new Date(job.completionDate._seconds * 1000);
      } else if (typeof job.completionDate.toDate === 'function') {
        jobCompletionDate = job.completionDate.toDate();
      } else {
        return false;
      }

      if (isNaN(jobCompletionDate.getTime())) return false;

      // Calculate days difference
      const timeDiff = Math.abs(proposedDate.getTime() - jobCompletionDate.getTime());
      const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

      return daysDiff <= windowDays;
    });

    console.log('[checkNearbyCompletedJobs] Nearby jobs found:', nearbyJobs.length);
    return nearbyJobs;

  } catch (error) {
    console.error('[checkNearbyCompletedJobs] Error:', error);
    return [];
  }
};

/**
 * Generate invoices for all completed jobs in a monthly allocation.
 * @param {string} scheduleId - The schedule ID.
 * @param {string} monthYear - The month and year (e.g., "November 2025").
 * @returns {Promise<object>} - Response with invoice generation results.
 * @throws {Error} - Throws an error if request fails.
 */
export const generateMonthlyAllocationInvoice = async (scheduleId, monthYear) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${API_BASE_URL}/job/generate-monthly-invoice`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ scheduleId, monthYear }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    if (responseData.status !== 'success') {
      throw new Error(responseData.message || 'Failed to generate monthly allocation invoice');
    }

    return responseData;
  } catch (error) {
    console.error("Error generating monthly allocation invoice:", error);
    throw error;
  }
};

/**
 * Gets earlier incomplete jobs for the same schedule (same client, same service).
 * Used when completing a job to prompt franchisee about earlier skipped jobs.
 * @param {Object} options - Options for the query.
 * @param {string} options.scheduleId - The schedule ID to find related jobs.
 * @param {string} options.currentJobId - The current job ID (to exclude from results).
 * @param {string} options.currentJobDate - The completion date of the current job (YYYY-MM-DD).
 * @param {string} options.fseId - The franchisee ID.
 * @returns {Promise<Array>} - Array of earlier incomplete jobs.
 */
export const getEarlierIncompleteJobs = async (options) => {
  const { scheduleId, currentJobId, currentJobDate } = options;

  if (!scheduleId || !currentJobId || !currentJobDate) {
    console.warn('[getEarlierIncompleteJobs] Missing required parameters');
    return [];
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();

  try {
    // The backend resolves the caller's identity and does the schedule/status/date filtering.
    const response = await fetch(`${API_BASE_URL}/job/franchisee/earlier-incomplete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ scheduleId, currentJobId, currentJobDate }),
    });

    if (!response.ok) {
      console.error('[getEarlierIncompleteJobs] Failed to fetch earlier incomplete jobs');
      return [];
    }

    const result = await response.json();
    const earlierJobs = result.status === 'success' && result.data?.jobs ? result.data.jobs : [];

    console.log('[getEarlierIncompleteJobs] Earlier incomplete jobs found:', earlierJobs.length);
    return earlierJobs;
  } catch (error) {
    console.error('[getEarlierIncompleteJobs] Error:', error);
    return [];
  }
};

/**
 * Cancels multiple jobs by setting their status to 'Cancelled'.
 * @param {Array<string>} jobIds - Array of job IDs to cancel.
 * @returns {Promise<Object>} - Result with success count and any errors.
 */
export const cancelJobs = async (jobIds) => {
  if (!jobIds || jobIds.length === 0) {
    return { successCount: 0, errors: [] };
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();
  const results = { successCount: 0, errors: [] };

  for (const jobId of jobIds) {
    try {
      const response = await fetch(`${API_BASE_URL}/job/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jobId,
          status: 'cancelled',
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          results.successCount++;
        } else {
          results.errors.push({ jobId, error: result.message || 'Update failed' });
        }
      } else {
        results.errors.push({ jobId, error: `HTTP ${response.status}` });
      }
    } catch (error) {
      results.errors.push({ jobId, error: error.message });
    }
  }

  console.log('[cancelJobs] Results:', results);
  return results;
};
